import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Icon,
  Table,
  Dropdown,
  Message,
} from "semantic-ui-react";
import {
  AlertNotificationRule,
  AlertRule,
  EmailNotificationParams,
  SlackNotificationParams,
  SmsNotificationParams,
  WebhookNotificationParams,
  WhatsAppNotificationParams,
} from "../../../../util";
import { ButtonIcon, DisplayIf, formatDuration } from "../../util";
import ConfirmationModal from "../../common/ConfirmationModal";
import ConfirmationModalMessage from "../../common/ConfirmationModalMessage";
import {
  createAlertNotificationRule,
  deleteAlertNotificationRule,
  updateAlertNotificationRule,
} from "../../../../BytebeamClient";
import { beamtoast } from "../../../common/CustomToast";
import styled from "styled-components";
import { useUser } from "../../../../context/User.context";
import TextWithToolTip from "../../DeviceManagement/Devices/TextWithToolTip";
import { Mixpanel } from "../../common/MixPanel";
import CreateNotificationRuleModal from "./CreateNotificationRule";
import { AlertRuleOperationType } from "../AlertRules/AlertRules";
import moment from "moment";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { CardContainer } from "../../../common/ActionsUtils";
import PopupWithLowZIndex from "../../common/PopupWithLowZIndex";
import { OffsetDiv } from "../../Settings/DBC/DBC";
import { DropdownOptionType } from "../../DeviceManagement/Devices/ActionModals/UpdateConfigModal";
import { STATIC_WARNING_TEXT } from "../../../common/StatusBar";

export enum NotificationRuleOperationType {
  Create = "create",
  Update = "update",
}

export enum NotificationModalStepType {
  ChooseNotificationType,
  FillNotificationTypeDetails,
}

interface DeleteAlertNotificationRuleButtonProps {
  readonly onChange: (showLoading: boolean) => void;
  readonly notificationRule: AlertNotificationRule;
  readonly alertRuleName: string;
}

function DeleteAlertNotificationRuleButton(
  props: DeleteAlertNotificationRuleButtonProps
) {
  const { alertRuleName, notificationRule, onChange } = props;
  const channelType = notificationRule.channel_type;

  return (
    <ConfirmationModal
      trigger={
        <ButtonIcon link name="trash" title="Delete Notification Rule" />
      }
      prefixContent="Delete Alert Notification Rule"
      expectedText={`${alertRuleName} - ${channelType}`}
      message={
        <ConfirmationModalMessage
          name={`${alertRuleName} - ${channelType}`}
          type={"Alert Notification Rule"}
          specialMessage={`Deleting this notification rule will stop notifications generated by the rule for the alert "${alertRuleName}"`}
        />
      }
      onConfirm={async () => {
        try {
          await deleteAlertNotificationRule(notificationRule);
          beamtoast.success(
            `Deleted alert notification rule "${alertRuleName} - ${channelType}"`
          );
        } catch (e) {
          beamtoast.error(
            `Failed to delete alert rule "${alertRuleName} - ${channelType}"`
          );
          console.log(e);
        } finally {
          onChange(true);
        }
      }}
    />
  );
}

const StyleNotificationChannelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  line-height: 18px;
  gap: 12px;
`;

const FilterDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 12px;
`;

type AlertNotificationRulesProps = {
  readonly alertRules: AlertRule[];
  readonly selectedChannelFilter: string;
  readonly onChannelFilterChange: (value: string) => void;
  readonly onChange: (showLoading: boolean) => void;
};

export default function AlertNotificationRules(
  props: AlertNotificationRulesProps
) {
  const { user } = useUser();
  const permissions = user?.role?.permissions;

  const { alertRules, selectedChannelFilter, onChannelFilterChange, onChange } =
    props;

  const [open, setOpen] = useState(false);
  const [notificationRule, setNotificationRule] = useState<
    AlertNotificationRule | undefined
  >();
  const [notificationModalStep, setNotificationModalStep] = useState<number>(
    NotificationModalStepType.ChooseNotificationType
  );
  const [operationType, setOperationType] = useState<string>(
    NotificationRuleOperationType.Create
  );

  const [allNotificationRules, setAllNotificationRules] = useState<
    AlertNotificationRule[]
  >([]);

  //state for filtering
  const [filteredAlertRules, setFilteredAlertRules] = useState<AlertRule[]>([]);
  const [channelFilterOptions, setChannelFilterOptions] = useState<
    DropdownOptionType[]
  >([]);

  const applyCurrentFilter = (rules: AlertRule[], currentFilter: string) => {
    if (!currentFilter) {
      return rules;
    }

    return rules
      .map((alertRule) => ({
        ...alertRule,
        notification_rules:
          alertRule.notification_rules?.filter(
            (rule) => rule.channel_type === currentFilter
          ) || [],
      }))
      .filter(
        (alertRule) =>
          alertRule.notification_rules &&
          alertRule.notification_rules.length > 0
      );
  };

  useEffect(() => {
    const extractedRules: AlertNotificationRule[] = [];

    if (alertRules && alertRules.length > 0) {
      alertRules.forEach((alertRule) => {
        if (
          alertRule.notification_rules &&
          alertRule.notification_rules.length > 0
        ) {
          extractedRules.push(...alertRule.notification_rules);
        }
      });
    }
    setAllNotificationRules(extractedRules);

    //Applying current filter to maintain the selected filter state
    const filtered = applyCurrentFilter(alertRules, selectedChannelFilter);
    setFilteredAlertRules(filtered);

    //filter options based on channel types with counts
    const channelTypeCounts: { [key: string]: number } = {};
    extractedRules.forEach((rule) => {
      channelTypeCounts[rule.channel_type] =
        (channelTypeCounts[rule.channel_type] || 0) + 1;
    });

    const uniqueChannelTypes = [
      ...new Set(extractedRules.map((rule) => rule.channel_type)),
    ];

    //channel types by count in descending order
    const sortedChannelTypes = uniqueChannelTypes.sort((a, b) => {
      return channelTypeCounts[b] - channelTypeCounts[a];
    });

    const options = [
      {
        key: "all",
        value: "",
        text: `All Channels (${extractedRules.length})`,
      },
      ...sortedChannelTypes.map((channelType) => ({
        key: channelType,
        value: channelType,
        text: `${channelType === "sms" ? "SMS" : channelType.charAt(0).toUpperCase() + channelType.slice(1)} (${channelTypeCounts[channelType]})`,
      })),
    ];
    setChannelFilterOptions(options);

    //reset filter if the selected channel type no longer exists
    const availableChannelTypes = uniqueChannelTypes.map((type) =>
      String(type)
    );
    if (
      selectedChannelFilter &&
      !availableChannelTypes.includes(selectedChannelFilter)
    ) {
      onChannelFilterChange("");
    }
  }, [alertRules, selectedChannelFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChannelFilterChange = (event: any, { value }: any) => {
    onChannelFilterChange(value);
    //Apply filter immediately using the new value
    const filtered = applyCurrentFilter(alertRules, value);
    setFilteredAlertRules(filtered);
  };

  const renderNotificationChannel = (
    notificationRule: AlertNotificationRule
  ) => {
    if (notificationRule.channel_type === "slack") {
      const params =
        notificationRule.channel_parameters as SlackNotificationParams;

      return (
        <StyleNotificationChannelWrapper>
          <Icon name="slack" style={{ fontSize: "17px", fontWeight: "900" }} />
          <TextWithToolTip text={params.slack_channel} />
        </StyleNotificationChannelWrapper>
      );
    } else if (notificationRule.channel_type === "email") {
      const params =
        notificationRule.channel_parameters as EmailNotificationParams;

      return (
        <StyleNotificationChannelWrapper>
          <Icon name="mail" style={{ fontSize: "15px" }} />
          <TextWithToolTip text={params.emails.join(", ")} />
        </StyleNotificationChannelWrapper>
      );
    } else if (notificationRule.channel_type === "webhook") {
      const params =
        notificationRule.channel_parameters as WebhookNotificationParams;

      return (
        <StyleNotificationChannelWrapper>
          <Icon name="external" style={{ fontSize: "15px" }} />
          <TextWithToolTip text={params.url} />
        </StyleNotificationChannelWrapper>
      );
    } else if (notificationRule.channel_type === "sms") {
      const params =
        notificationRule.channel_parameters as SmsNotificationParams;

      return (
        <StyleNotificationChannelWrapper>
          <Icon name="comment" style={{ fontSize: "15px" }} />
          <TextWithToolTip text={params.phone_numbers.join(", ")} />
        </StyleNotificationChannelWrapper>
      );
    } else if (notificationRule.channel_type === "whatsapp") {
      const params =
        notificationRule.channel_parameters as WhatsAppNotificationParams;

      return (
        <StyleNotificationChannelWrapper>
          <Icon
            name="whatsapp"
            style={{ fontSize: "17px", fontWeight: "900" }}
          />
          <TextWithToolTip text={params.phone_numbers.join(", ")} />
        </StyleNotificationChannelWrapper>
      );
    }
  };

  const renderDeleteButton = (
    alertRule: AlertRule,
    notificationRule: AlertNotificationRule
  ) => {
    return (
      <DeleteAlertNotificationRuleButton
        notificationRule={notificationRule}
        alertRuleName={alertRule.name}
        onChange={onChange}
      />
    );
  };

  const doNotificationRulesExist = () => {
    let exist: boolean = false;

    if (alertRules.length !== 0) {
      for (const alertRule of alertRules) {
        if (alertRule.notification_rules?.length !== 0) {
          exist = true;
          break;
        }
      }
    }

    return exist;
  };

  const handleCreateAlertNotificationRule = async (
    notificationRule: AlertNotificationRule
  ) => {
    const { channel_type: channelType, alert_rule_id: alertRuleId } =
      notificationRule;
    const alertRuleName = alertRules.find(
      (rule) => rule.id === alertRuleId
    )?.name;

    try {
      await createAlertNotificationRule(notificationRule);
      beamtoast.success(
        `Created alert notification rule "${alertRuleName} - ${channelType}"`
      );
      Mixpanel.track("Created Notification Rule", {});
    } catch (e) {
      console.error("Error creating alert notification rule:", e);
      beamtoast.error(
        `Failed to create alert notification rule "${alertRuleName} - ${channelType}"`
      );
      Mixpanel.track("Failure", {
        type: "Alert Notification Rule creation",
        error: JSON.stringify(e instanceof Error ? e.message : e),
      });
    } finally {
      onChange(true);
    }
  };

  const handleEditAlertNotificationRule = async (
    notificationRule: AlertNotificationRule
  ) => {
    const { channel_type: channelType, alert_rule_id: alertRuleId } =
      notificationRule;
    const alertRuleName = alertRules.find(
      (rule) => rule.id === alertRuleId
    )?.name;

    try {
      await updateAlertNotificationRule(notificationRule);
      beamtoast.success(
        `Updated alert notification rule "${alertRuleName} - ${channelType}"`
      );
      Mixpanel.track("Updated Notification Rule", {});
    } catch (e) {
      console.error("Error editing alert notification rule:", e);
      beamtoast.error(
        `Failed to edit alert notification rule "${alertRuleName} - ${channelType}"`
      );
      Mixpanel.track("Failure", {
        type: "Alert Notification Rule editing",
        error: JSON.stringify(e instanceof Error ? e.message : e),
      });
    } finally {
      onChange(true);
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setOperationType(NotificationRuleOperationType.Create);
    setNotificationRule(undefined);
  };

  return (
    <CardContainer>
      <Grid>
        <CreateNotificationRuleModal
          open={open}
          onOpen={() => setOpen(true)}
          onClose={handleModalClose}
          title={
            operationType === NotificationRuleOperationType.Create
              ? "New Notification Rule"
              : "Edit Notification Rule"
          }
          onSubmit={
            operationType === NotificationRuleOperationType.Create
              ? handleCreateAlertNotificationRule
              : handleEditAlertNotificationRule
          }
          alertRules={alertRules}
          alertNotificationRule={notificationRule}
          notificationModalStep={notificationModalStep}
          operationType={
            operationType === NotificationRuleOperationType.Create
              ? "create"
              : "update"
          }
          existingNotificationRules={allNotificationRules}
        />
        <Grid.Row>
          <Grid.Column>
            <Message
              content={`WARNING: ${STATIC_WARNING_TEXT}`}
              warning
              style={{ padding: 8, fontWeight: 700 }}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div></div> {/* Empty div for spacing */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <FilterDropdownWrapper>
                  <Icon
                    name="filter"
                    style={{
                      fontSize: "1.25rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                  <Dropdown
                    search
                    selection
                    style={{ minWidth: "180px" }}
                    options={channelFilterOptions}
                    value={selectedChannelFilter}
                    placeholder="Filter by channel"
                    onChange={handleChannelFilterChange}
                  />
                </FilterDropdownWrapper>
                <DisplayIf cond={permissions.editNotifications}>
                  <Button
                    id="addNotificationRuleButton"
                    primary
                    icon
                    labelPosition="left"
                    onClick={() => {
                      setOperationType(AlertRuleOperationType.Create);
                      setNotificationModalStep(
                        NotificationModalStepType.ChooseNotificationType
                      );
                      setOpen(true);
                    }}
                  >
                    <Icon name="plus" />
                    Notification Rule
                  </Button>
                </DisplayIf>
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Table id="notificationRulesTable" celled fixed>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Alert Name</Table.HeaderCell>
                  <Table.HeaderCell width={4}>
                    Notification Channel
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2}>Interval</Table.HeaderCell>
                  <Table.HeaderCell>Last Notified At</Table.HeaderCell>
                  <Table.HeaderCell>Timezone</Table.HeaderCell>
                  <DisplayIf cond={permissions.editNotifications}>
                    <Table.HeaderCell width={2} textAlign="center">
                      Options
                    </Table.HeaderCell>
                  </DisplayIf>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {filteredAlertRules.length !== 0 &&
                doNotificationRulesExist() ? (
                  filteredAlertRules.map((alertRule) => {
                    return alertRule.notification_rules?.map(
                      (notificationRule) => {
                        return (
                          <Table.Row key={notificationRule.id}>
                            <Table.Cell>
                              <TextWithToolTip
                                text={notificationRule.name ?? "Untitled"}
                                textList={[`ID: ${notificationRule.id}`]}
                                copyContentWithTextList={true}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <TextWithToolTip text={alertRule.name} />
                            </Table.Cell>
                            <Table.Cell>
                              {renderNotificationChannel(notificationRule)}
                            </Table.Cell>
                            <Table.Cell>
                              {formatDuration(
                                notificationRule.interval_seconds
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {notificationRule.last_notified_at ? (
                                <PopupWithLowZIndex
                                  content={moment(
                                    notificationRule.last_notified_at
                                  ).format("MMMM Do YYYY, h:mm:ss a")}
                                  trigger={
                                    <OffsetDiv>
                                      <span style={{ paddingRight: "5px" }} />
                                      {moment
                                        .duration(
                                          notificationRule.last_notified_at -
                                            new Date().valueOf()
                                        )
                                        .humanize()}{" "}
                                      ago
                                    </OffsetDiv>
                                  }
                                />
                              ) : (
                                "--"
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {notificationRule.timezone ?? "UTC"}
                            </Table.Cell>
                            <DisplayIf cond={permissions.editNotifications}>
                              <Table.Cell>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexWrap: "nowrap",
                                    gap: "16px",
                                  }}
                                >
                                  <ButtonIcon
                                    link
                                    name="edit"
                                    title="Edit Notification Rule"
                                    onClick={() => {
                                      setNotificationRule(notificationRule);
                                      setOperationType(
                                        NotificationRuleOperationType.Update
                                      );
                                      setNotificationModalStep(
                                        NotificationModalStepType.FillNotificationTypeDetails
                                      );
                                      setOpen(true);
                                    }}
                                  />
                                  {renderDeleteButton(
                                    alertRule,
                                    notificationRule
                                  )}
                                </div>
                              </Table.Cell>
                            </DisplayIf>
                          </Table.Row>
                        );
                      }
                    );
                  })
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan={permissions.editNotifications ? 7 : 6}>
                      <ErrorMessage message={"No Notification Rules found!"} iconSize="large"  />
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </CardContainer>
  );
}
